/**
 * NotFound — custom 404 page used as the catch-all for unknown routes.
 * Mantém o layout principal (Header + Footer via MainLayout) e oferece
 * caminhos amigáveis: voltar para a home, explorar negócios, contato.
 */
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Search, Compass, ArrowLeft } from 'lucide-react';
import useMunicipioBasePath from '../hooks/useMunicipioBasePath';

export default function NotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const { basePath } = useMunicipioBasePath();

  return (
    <div
      className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-gradient-to-br from-purple-50 via-white to-orange-50"
      data-testid="not-found-page"
    >
      <div className="max-w-2xl w-full text-center">
        <div className="relative inline-block mb-6">
          <div
            className="text-[120px] sm:text-[180px] leading-none font-black text-[var(--primary)] select-none"
            style={{ fontFamily: 'Manrope' }}
          >
            404
          </div>
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-32 h-3 bg-[var(--secondary)] rounded-full blur-md opacity-60" />
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Manrope' }}>
          Não encontrado
        </h1>
        <p className="text-base text-gray-600 leading-relaxed max-w-md mx-auto mb-2">
          A página que você procura não existe, foi movida ou o link está com erro de digitação.
        </p>
        {location?.pathname && (
          <p className="text-xs text-gray-400 font-mono mb-8 break-all">{location.pathname}</p>
        )}

        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to={basePath || '/'}
            className="inline-flex items-center gap-2 bg-[var(--primary)] text-white hover:bg-[#450066] rounded-full px-6 py-3 font-semibold transition-all shadow-md hover:shadow-lg active:scale-95"
            data-testid="not-found-home-link"
          >
            <Home className="w-4 h-4" /> Voltar para a home
          </Link>
          <Link
            to={`${basePath}/explorar`}
            className="inline-flex items-center gap-2 bg-white border border-[var(--border)] text-gray-700 hover:bg-gray-50 rounded-full px-6 py-3 font-semibold transition-all"
            data-testid="not-found-explore-link"
          >
            <Compass className="w-4 h-4" /> Explorar negócios
          </Link>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 px-4 py-3 text-sm"
            data-testid="not-found-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
        </div>

        <div className="mt-10 pt-8 border-t border-gray-200/70">
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-3 inline-flex items-center gap-1">
            <Search className="w-3 h-3" /> Talvez você esteja procurando
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 text-sm">
            <Link to={`${basePath}/categorias`} className="text-[var(--primary)] hover:underline">Categorias</Link>
            <span className="text-gray-300">·</span>
            <Link to={`${basePath}/campanhas`} className="text-[var(--primary)] hover:underline">Promoções</Link>
            <span className="text-gray-300">·</span>
            <Link to={`${basePath}/noticias`} className="text-[var(--primary)] hover:underline">Notícias</Link>
            <span className="text-gray-300">·</span>
            <Link to={`${basePath}/social`} className="text-[var(--primary)] hover:underline">Social</Link>
            <span className="text-gray-300">·</span>
            <Link to={`${basePath}/fale-conosco`} className="text-[var(--primary)] hover:underline">Fale conosco</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
